import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export type ChangelogEntry = Database["public"]["Tables"]["changelog_entries"]["Row"];
export type AlgorithmUpdate = Database["public"]["Tables"]["algorithm_updates"]["Row"];
export type VolatilitySnapshot = Database["public"]["Tables"]["volatility_snapshots"]["Row"];

export const listChangelogEntries = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("changelog_entries")
    .select("*")
    .eq("is_published", true)
    .order("occurred_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as ChangelogEntry[];
});

export const getChangelogEntry = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("changelog_entries")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row ?? null) as ChangelogEntry | null;
  });

export const listAlgorithmUpdates = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("algorithm_updates")
    .select("*")
    .eq("is_published", true)
    .order("started_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as AlgorithmUpdate[];
});

export const getAlgorithmUpdate = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("algorithm_updates")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row ?? null) as AlgorithmUpdate | null;
  });

export const listVolatilitySnapshots = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("volatility_snapshots")
    .select("*")
    .order("snapshot_date", { ascending: false })
    .limit(52);
  if (error) throw new Error(error.message);
  return (data ?? []) as VolatilitySnapshot[];
});

export type RelatedArticle = {
  slug: string;
  title: string;
  excerpt: string | null;
  published_at: string | null;
};

export const listArticlesInRange = createServerFn({ method: "GET" })
  .inputValidator((input: { from: string; to: string; limit?: number }) => input)
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: rows, error } = await supabase
      .from("articles")
      .select("slug, title, excerpt, published_at")
      .eq("status", "published")
      .gte("published_at", data.from)
      .lte("published_at", data.to)
      .order("published_at", { ascending: false })
      .limit(data.limit ?? 6);
    if (error) throw new Error(error.message);
    return (rows ?? []) as RelatedArticle[];
  });

